import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { api } from "../../App";
import { styledToast as toast } from "@/components/ui/sonner";

export default function FarmerLoginForm({ formData, setFormData, t }) {
  const [otpSent, setOtpSent] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [displayOtp, setDisplayOtp] = useState("");

  const handleSendOTP = async () => {
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (!phoneDigits) {
      toast.error(t('validation.phoneRequired'));
      return { success: false };
    }
    if (phoneDigits.length !== 10) {
      toast.error(t('validation.phoneInvalid'));
      return { success: false };
    }
    
    setLoading(true);
    try {
      const response = await api.post("/auth/send-otp", {
        phone: phoneDigits,
        role: "farmer",
        department: null,
      });
      setOtpSent(true);
      toast.success(`${t('common.otpSentTo')} ${phoneDigits}`);
      if (response.data.otp) {
        setDisplayOtp(response.data.otp);
      }
      return { success: true, waitForOtp: true };
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.failedToSendOtp'));
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otpValue) {
      toast.error(t('common.enterOtp'));
      return { success: false };
    }

    setLoading(true);
    try {
      const response = await api.post("/auth/verify-otp", {
        phone: formData.phone,
        otp: otpValue,
      });
      return { 
        success: true, 
        token: response.data.access_token, 
        user: response.data.user 
      };
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.invalidOtp'));
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!otpSent) {
      return handleSendOTP();
    } else {
      return handleVerifyOTP();
    }
  };

  const getButtonText = () => {
    if (loading) return t('common.processing');
    return otpSent ? t('login.verifyOTP') : t('login.sendOTP');
  };

  return {
    render: (
      <>
        <div>
          <Label htmlFor="phone">{t('login.phone')}</Label>
          <Input
            id="phone"
            type="tel"
            required
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="mt-1.5"
            placeholder="+91 XXXXX XXXXX"
            data-testid="input-phone"
            disabled={otpSent}
          />
        </div>
        {otpSent && (
          <div>
            {displayOtp && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 mb-2 text-center" data-testid="otp-display-banner">
                <p className="text-xs text-amber-700 font-medium mb-1">SMS gateway pending — OTP shown here for now</p>
                <p className="text-2xl font-bold tracking-[0.3em] text-amber-900" data-testid="otp-display-code">{displayOtp}</p>
              </div>
            )}
            <Label htmlFor="otp">{t('login.enterOTP')}</Label>
            <Input
              id="otp"
              type="text"
              required
              value={otpValue}
              onChange={(e) => setOtpValue(e.target.value)}
              className="mt-1.5"
              placeholder={t('login.otpPlaceholder')}
              maxLength={6}
              data-testid="input-otp"
            />
            <button
              type="button"
              onClick={() => {
                setOtpSent(false);
                setOtpValue("");
                setDisplayOtp("");
              }}
              className="text-xs text-emerald-800 mt-1 hover:underline"
            >
              {t('login.changePhone')}
            </button>
          </div>
        )}
      </>
    ),
    handleSubmit,
    getButtonText,
    loading,
    otpSent
  };
}
